import React, { useMemo, useState } from 'react';

import { Dropdown } from 'antd';

import { useProjectContext } from '@contexts/ProjectContext';
import { useUserPermission } from '@contexts/UserContext';

import CustomSwitch from '@components/Gui/CustomSwitch';
import { NwAddButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import AddProjectTransactionDrawer from '@components/Project/ProjectTransactions/Transactions/AddProjectTransactionDrawer';
import ProjectGroupedTransactionsList from '@components/Project/ProjectTransactions/Transactions/ProjectGroupedTransactionsList';

import ProjectGroupedStatements from './ProjectGroupedStatements';
import { StyledTabs } from './ProjectTransactionsUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ProjectTransactionsCard = ({ readOnly }) => {
    const { projectId, projectData, transactionsGrouped } = useProjectContext();
    const [transactionGroup, setTransactionGroup] = useState();
    const [transactionDrawer, setTransactionDrawer] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const [tab, setTab] = useState('summary');

    const projectTransactions = transactionsGrouped || [];

    const hasBlockingTransactions = useMemo(() => {
        if (!projectTransactions || !projectTransactions.length) return false;

        const blockingFamilies = ['JobFee', 'Product', 'JobAdvance', 'AgFee', 'JobCredit'];

        for (const modelTransaction of projectTransactions) {
            if (modelTransaction.Dates && modelTransaction.Dates.length) {
                for (const date of modelTransaction.Dates) {
                    if (date.Transactions && date.Transactions.length) {
                        const hasBlocking = date.Transactions.some((transaction) =>
                            blockingFamilies.includes(transaction.Family)
                        );
                        if (hasBlocking) return true;
                    }
                }
            }
        }
        return false;
    }, [projectTransactions]);

    const hasCommissionAsMotherAgency = useMemo(() => {
        if (!projectTransactions || !projectTransactions.length) return false;

        for (const modelTransaction of projectTransactions) {
            if (modelTransaction.Dates && modelTransaction.Dates.length) {
                for (const date of modelTransaction.Dates) {
                    if (date.Transactions && date.Transactions.length) {
                        const hasCommAsMothAg = date.Transactions.some(
                            (transaction) => transaction.Family === 'CommAsMothAg'
                        );
                        if (hasCommAsMothAg) return true;
                    }
                }
            }
        }
        return false;
    }, [projectTransactions]);

    // Check if project has events of type 'ext' (required for CommissionAsMotherAgency)
    const hasExtEvents = useMemo(() => {
        if (!projectData?.Events) return false;
        return projectData.Events.some((event) => event.TypeName?.toLowerCase() === 'ext');
    }, [projectData]);

    // Check if project has events of type 'job' or 'casting' (required for JobFee)
    const hasJobOrCastingEvents = useMemo(() => {
        if (!projectData?.Events) return false;
        return projectData.Events.some(
            (event) => event.TypeName?.toLowerCase() === 'job' || event.TypeName?.toLowerCase() === 'casting'
        );
    }, [projectData]);

    const handleShowTransactionDrawer = ({ key }) => {
        setTransactionGroup(key);
        setTransactionDrawer(true);
    };

    const ViewAgencyFee = useUserPermission('ViewAgencyFee');
    const ViewAgencyCommission = useUserPermission('ViewAgencyCommission');
    const ViewServiceStatement = useUserPermission('ViewServiceStatement');
    const ViewMotherAgencyCommission = useUserPermission('ViewMotherAgencyCommission');

    // const ViewModelStatement = useUserPermission("ViewModelStatement")
    // const ViewAgencyStatement = useUserPermission("ViewAgencyStatement")
    // const ViewClientStatement = useUserPermission("ViewClientStatement")

    const canAccessTab = (type) => {
        switch (type) {
            case 'models':
                return ViewAgencyCommission;
            //return ViewAgencyCommission && ViewModelStatement;
            case 'clients':
                return ViewAgencyFee;
            //return ViewAgencyFee && ViewClientStatement;
            case 'agencies':
                return ViewMotherAgencyCommission;
            //return ViewAgencyStatement && ViewMotherAgencyCommission;
            case 'services':
                return ViewServiceStatement;
            default:
                return false;
        }
    };

    const renderAddTransaction = () => {
        return [
            <Dropdown
                key="addTransaction"
                menu={{
                    onClick: handleShowTransactionDrawer,
                    items: [
                        {
                            key: 'Fee',
                            label: 'Fee',
                            disabled: hasCommissionAsMotherAgency || !hasJobOrCastingEvents,
                        },
                        { key: 'Expense', label: 'Expense' },
                        { key: 'Credit', label: 'Credit', disabled: hasCommissionAsMotherAgency },
                        {
                            key: 'CommissionAsMotherAgency',
                            label: 'Commission As Mother Agency',
                            disabled: hasBlockingTransactions || !hasExtEvents,
                        },
                    ],
                }}
            >
                <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                    <NwAddButton ghost size="small" label="add a transaction" />
                </div>
            </Dropdown>,
        ];
    };

    const ExpandSwitch = () => <CustomSwitch value={isExpanded} text="expand details" onChange={setIsExpanded} />;

    const handleCloseTransactionDrawer = () => {
        setTransactionDrawer(false);
        setTransactionGroup();
    };

    return (
        <NwCard
            title="Transactions"
            icon={byPrefixAndName.fasl['message-dollar']}
            extra={!readOnly && renderAddTransaction()}
        >
            <StyledTabs
                activeKey={tab}
                onChange={setTab}
                tabBarExtraContent={
                    <div style={{ paddingRight: '1rem' }}>
                        <ExpandSwitch />
                    </div>
                }
                items={[
                    {
                        key: 'summary',
                        label: 'Summary',
                        children: <ProjectGroupedTransactionsList readOnly={readOnly} isExpanded={isExpanded} />,
                    },
                    {
                        key: 'clients',
                        label: 'Clients',
                        disabled: !canAccessTab('clients'),
                        children: (
                            <ProjectGroupedStatements
                                readOnly={readOnly}
                                statementType="Client"
                                isExpanded={isExpanded}
                            />
                        ),
                    },
                    {
                        key: 'models',
                        label: 'Models',
                        disabled: !canAccessTab('models'),
                        children: (
                            <ProjectGroupedStatements
                                readOnly={readOnly}
                                statementType="Model"
                                isExpanded={isExpanded}
                            />
                        ),
                    },
                    {
                        key: 'services',
                        label: 'Services',
                        disabled: !canAccessTab('services'),
                        children: (
                            <ProjectGroupedStatements
                                readOnly={readOnly}
                                statementType="Service"
                                isExpanded={isExpanded}
                            />
                        ),
                    },
                    {
                        key: 'agencies',
                        label: 'Agencies',
                        disabled: !canAccessTab('agencies'),
                        children: (
                            <ProjectGroupedStatements
                                readOnly={readOnly}
                                statementType="Agency"
                                isExpanded={isExpanded}
                            />
                        ),
                    },
                ]}
            />
            {transactionDrawer && (
                <AddProjectTransactionDrawer
                    transactionGroup={transactionGroup}
                    projectId={projectId}
                    onClose={handleCloseTransactionDrawer}
                />
            )}
        </NwCard>
    );
};

export default ProjectTransactionsCard;
